import { useState } from "react";
import { Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { librarySectionHeadingClassName } from "@/components/library/libraryNavStyles";
import {
  libraryContainerCardWrapClassName,
  libraryContainerGridClassName,
  LIBRARY_SINGLE_ROW_SEE_ALL_THRESHOLD,
} from "@/components/library/libraryCompactRowLayout";
import { DroppableLibrarySpace } from "@/components/library/LibraryDroppables";
import type { MastermindSpace } from "@shared/schema";

export type LibrarySpaceCard = {
  id: string;
  spaceId: string;
  name: string;
  description?: string | null;
  documentCount: number;
};

type LibrarySpacesSectionProps = {
  spaces: LibrarySpaceCard[];
  onSpaceClick: (spaceFolderId: string) => void;
  title?: string;
};

export function librarySpacesFromMastermind(
  spaces: MastermindSpace[],
  getDocumentCount: (spaceId: string) => number
): LibrarySpaceCard[] {
  const seen = new Set<string>();
  const result: LibrarySpaceCard[] = [];
  for (const s of spaces) {
    if (seen.has(s.id)) continue;
    seen.add(s.id);
    result.push({
      id: `space-${s.id}`,
      spaceId: s.id,
      name: s.name ?? "Unnamed Space",
      description: s.description,
      documentCount: getDocumentCount(s.id),
    });
  }
  return result;
}

export function LibrarySpacesSection({
  spaces,
  onSpaceClick,
  title = "Spaces",
}: LibrarySpacesSectionProps) {
  const [showAll, setShowAll] = useState(false);

  if (spaces.length === 0) return null;

  const hasMore = spaces.length > LIBRARY_SINGLE_ROW_SEE_ALL_THRESHOLD;

  return (
    <section className="mb-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className={librarySectionHeadingClassName}>{title}</h2>
        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs shrink-0"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? "Show one row" : `See all (${spaces.length})`}
          </Button>
        )}
      </div>
      <div className={libraryContainerGridClassName(showAll)}>
        {spaces.map((space) => (
          <div key={space.id} className={libraryContainerCardWrapClassName(showAll)}>
            <DroppableLibrarySpace spaceId={space.spaceId}>
              <Card
                className="group hover:shadow-md transition-shadow cursor-pointer border-purple-200 bg-purple-50/50 dark:bg-purple-950/30 dark:border-purple-800 h-full"
                onClick={() => onSpaceClick(space.id)}
                data-testid={`space-folder-${space.spaceId}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Brain className="h-4 w-4 text-purple-600 shrink-0" />
                    <Badge
                      variant="outline"
                      className="text-xs bg-purple-100 border-purple-300 text-purple-800 dark:bg-purple-900/50 dark:border-purple-700 dark:text-purple-200"
                    >
                      Space
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-sm line-clamp-2 leading-snug">{space.name}</h3>
                  {space.description ? (
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-1">{space.description}</p>
                  ) : null}
                  <p className="text-xs text-muted-foreground mt-1">
                    {space.documentCount}{" "}
                    {space.documentCount === 1 ? "document" : "documents"}
                  </p>
                </CardContent>
              </Card>
            </DroppableLibrarySpace>
          </div>
        ))}
      </div>
    </section>
  );
}
